import { useMemo, useSyncExternalStore } from 'react';
import type { TimerPreset } from '@susdle/shared';
import type { LobbySession, Snapshot } from './LobbySession.js';
import type { LobbyView } from '../lobby/types.js';

export interface UseLobbySessionResult {
  view: LobbyView | null;
  bootPhase: Snapshot['bootPhase'];
  error: Snapshot['error'];
  /**
   * Connection state lifted out of LobbyView (it was redundantly threaded
   * through every phase view). The App-level connection banner reads this
   * directly so the banner mechanism is phase-agnostic.
   */
  connection: Snapshot['connection'];
  handlers: {
    onFoolToggle: (enabled: boolean) => void;
    onTimerChange: (preset: TimerPreset) => void;
    onStart: () => void;
    onLeave: () => void;
  };
}

export function useLobbySession(session: LobbySession): UseLobbySessionResult {
  const snapshot = useSyncExternalStore(
    (cb) => session.subscribe(cb),
    () => session.getSnapshot(),
  );
  const handlers = useMemo(() => ({
    onFoolToggle: (v: boolean) => session.sendFoolToggle(v),
    onTimerChange: (p: TimerPreset) => session.sendTimerChange(p),
    onStart: () => session.sendStart(),
    onLeave: () => { void session.leave(); },
  }), [session]);
  return {
    view: snapshot.view,
    bootPhase: snapshot.bootPhase,
    error: snapshot.error,
    connection: snapshot.connection,
    handlers,
  };
}
